import React from 'react';
import AcceptProofRequestAnimation from './lib/acceptProofRequestAnimation';
import AcceptCredentialOfferAnimation from './lib/acceptCredentialOfferAnimation';
import AddingToWallet from './lib/AddingToWallet';

type AnimationType = 'fadeUp' | 'fadeDown' | 'fadeIn' | 'scale' | 'custom';
type AnimationComponent = 'proof' | 'credential' | 'wallet';

interface AnimationConfig {
  from: Record<string, any>;
  to: Record<string, any>;
  delay?: number;
}

class AnimationFactory {
  private static defaultConfigs: Record<AnimationType, AnimationConfig> = {
    fadeUp: {
      from: { opacity: 0, transform: 'translateY(40px)' },
      to: { opacity: 1, transform: 'translateY(0)' }
    },
    fadeDown: {
      from: { opacity: 0, transform: 'translateY(-40px)' },
      to: { opacity: 1, transform: 'translateY(0)' }
    },
    fadeIn: {
      from: { opacity: 0 },
      to: { opacity: 1 }
    },
    scale: {
      from: { opacity: 0, scale: 0.8 },
      to: { opacity: 1, scale: 1 }
    },
    custom: {
      from: {},
      to: {}
    }
  };

  static createAnimation = (type: AnimationComponent): React.ReactNode => {
    switch (type) {
      case 'proof':
        return <AcceptProofRequestAnimation />;
      case 'credential':
        return <AcceptCredentialOfferAnimation />;
      case 'wallet':
        return <AddingToWallet />;
      default:
        return null;
    }
  };

  static getAnimation(
    type: AnimationType,
    customConfig?: Partial<AnimationConfig>
  ): () => AnimationConfig {
    return () => {
      const baseConfig = this.defaultConfigs[type];
      if (customConfig) {
        return {
          from: { ...baseConfig.from, ...customConfig.from },
          to: { ...baseConfig.to, ...customConfig.to }
        };
      }
      return baseConfig;
    };
  }

  static withDelay(type: AnimationType, delay: number): () => AnimationConfig {
    return () => ({
      ...this.defaultConfigs[type],
      to: {
        ...this.defaultConfigs[type].to,
        delay
      }
    });
  }
}

export default AnimationFactory;
